import { useState, useCallback } from "react";
import { toast } from "react-toastify";

const MESSAGES = {
  fetchError: "配信設定の取得に失敗しました",
  saveSuccess: "保存しました",
  saveError: "配信設定の保存に失敗しました",
};

// バックエンドは Ruby の wday 基準: 0=日(Sun), 1=月(Mon), ..., 6=土(Sat)
// フロントエンドは weekdayLabels の配列インデックス: 0=月(Mon), ..., 6=日(Sun)
const toBackendWeek = (frontendIndex) => (frontendIndex + 1) % 7;
const toFrontendIndex = (backendWeek) => (backendWeek + 6) % 7;

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.content || "";

/**
 * API から取得したスケジュールデータをフロントエンドの settings 配列に変換する
 *
 * @param {Object} apiSchedules - API レスポンスの schedules オブジェクト
 * @param {Array<{title: string, category: string}>} settingDefs - 設定定義（順序が settings 配列の順序になる）
 * @returns {Array} フロントエンド用の settings 配列
 */
const apiSchedulesToSettings = (apiSchedules, settingDefs) => {
  return settingDefs.map(({ title, category }) => {
    const cat = apiSchedules?.[category];
    const weekdays = (cat?.weekdays ?? [])
      .map(toFrontendIndex)
      .sort((a, b) => a - b);

    const [rawHour, rawMinute] = (cat?.send_time ?? "09:00").split(":");
    const hour = (rawHour ?? "09").padStart(2, "0");
    const rawMin = (rawMinute ?? "00").padStart(2, "0");
    const minute = ["00", "15", "30", "45"].includes(rawMin) ? rawMin : "00";

    return { title, weekdays, hour, minute, active: cat?.active ?? true };
  });
};

/**
 * メルマガ配信スケジュールの取得・保存を管理するカスタムフック
 */
export default function useMailMagazineSchedules() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/admin/mail_magazine_schedules.json", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!res.ok) throw new Error(MESSAGES.fetchError);

      const data = await res.json();
      return data.schedules;
    } catch (e) {
      if (e.name !== "AbortError") toast.error(MESSAGES.fetchError);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * settings 配列を受け取り、バックエンドへ保存する
   *
   * @param {Array} settings - フロントエンドの settings 配列
   * @param {Array<{title: string, category: string}>} settingDefs - カテゴリ対応定義
   * @returns {{ schedules: Object|null }|null} 成功時は { schedules } オブジェクト（schedules はレスポンス読み取り失敗時 null）、失敗時は null
   */
  const saveSchedules = useCallback(async (settings, settingDefs) => {
    setSaving(true);
    try {
      const body = {};
      settingDefs.forEach(({ category }, i) => {
        const s = settings[i];
        body[category] = {
          weekdays: s.weekdays.map(toBackendWeek),
          send_time: `${s.hour}:${s.minute}`,
        };
      });

      const res = await fetch("/admin/mail_magazine_schedules.json", {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ mail_magazine_schedules: body }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || MESSAGES.saveError);
      }

      const data = await res.json().catch(() => null);
      toast.success(MESSAGES.saveSuccess);
      return { schedules: data?.schedules ?? null };
    } catch (e) {
      toast.error(e.message || MESSAGES.saveError);
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const toggleActive = useCallback(async (category, active, title) => {
    setSaving(true);
    try {
      const res = await fetch("/admin/mail_magazine_schedules/toggle_active", {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({ category, active }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || MESSAGES.saveError);
      }

      const data = await res.json().catch(() => null);
      if (!data?.schedules) throw new Error(MESSAGES.saveError);
      toast.success(active ? `${title}の配信を開始しました` : `${title}の配信を停止しました`);
      return { schedules: data.schedules };
    } catch (e) {
      toast.error(e.message || MESSAGES.saveError);
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  return { loading, saving, fetchSchedules, saveSchedules, apiSchedulesToSettings, toggleActive };
}
